/**
 * 纪念日掠影第二版（P4d，design §11.11）的纯逻辑：
 * - 路线：整周年沿金色的纪念日射线、生日沿淡玫瑰色的生日射线（就是 semantic.ts 画的那两条——
 *   每年的同一天本来就在同一个方向上），满 100 天沿螺线的解析式（连续天数，不是每天一个锚点的折线；
 *   光带还是画折线，glimpsefx 只认它）。路线是纯几何，没有照片也照飞。
 * - 时间轴：进场 → 第一站停一拍 → 逐站飞一段、停一拍 → 终点停 → 退场。路线上统一用弧长 s
 *   （u = s/L）：停站用恒定速度 VD 微推（第一站先从 0 平滑升到 VD），fly 用「两端速度给定 +
 *   smootherstep 补差」的 s(t)，段与段的接缝处速度、加速度都连续；最后一段 fly 零速到站，
 *   终点那段 u 不动，镜头和注视点一起沿前方再推一点。
 * - 镜头：机位是 (注视点 Q, 朝向 ψ, 仰角 θ, 距离 d)。跟拍位由路线前方和 BACK/SIDE/UP 定；
 *   进退场在机位之间插（距离对数插、朝向走最近角、注视点按距离加权——远多挪近少挪），时长取各项
 *   运动限速里最大的那个下限，不设上限（上限会把限速破掉）。出发和落地分毫不差地是开始那一刻的镜头。
 * - 「收」（glimpseReturn）：六个坐标从收那一刻的机位（含三点差分出的速度、加速度）沿五次
 *   Hermite 平滑接回出发机位，时长服从同样的限速。
 * 不碰 three.js，好写单元测试；光带、光标、涟漪、字在 glimpsefx.ts，接管、收、让位在 galaxy.ts。
 */
import {
  SPIRAL,
  addDays,
  dayAngle,
  dayRadius,
  daysBetween,
  daysInMonth,
  formatLocalDate,
  isMonthDay,
  monthDayIn,
  parseLocalDate,
  yearRadius,
  type LocalDate,
  type Vec3,
} from '@star-sea/shared';
import { dotted } from './capsule.ts';
import { trailPath, type TrailPath } from './path.ts';

// ── 数字都集中在这一节：浏览器里看效果时调 ──
// 节奏
/** 中途站想停这么久；终点停 FINAL_S（in/out/final 不压） */
const DWELL_S = 0.45;
const FINAL_S = 1.4;
/** 停站微推的恒定速度（单位/秒）：想要的停站时长里正好推 PUSH_LEN 远；压缩只缩短推的距离，速度不变 */
const VD = 0.25 / 0.45;
const PUSH_LEN = 0.25;
/** 第一站停拍从 0 平滑升到 VD 用这么久（超过停拍时长一半就减半） */
const RAMP_S = 0.2;
/** 射线 fly 想要的时长 = 这一段的长度 / FLY_V，夹在 FLY_MIN–FLY_MAX */
const FLY_V = 12;
const FLY_MIN = 0.55;
const FLY_MAX = 1.6;
/**
 * 百天的弧只飞一段：想要 max(长度/12, T35)（T35 是「转向峰值 ≤ 35°/s」的最短时长），夹在
 * ARC_MIN–ARC_MAX；再硬叠 T90（90°/s 上限）和平移限速的下限，压也不能破
 */
const ARC_MIN = 3;
const ARC_MAX = 6;
/** 可压的段（第一站停拍、所有 fly、中间站停拍）共享的预算：软目标 15 秒减去不压的进、退、终点 */
const PACK_S = 11;
const TOTAL_SOFT = 15;
const FLY_FLOOR = 0.35;
const DWELL_FLOOR = 0.28;
/** 进、退场时长下限；上限没有——上限会把限速破掉 */
const IN_MIN = 1.3;
const OUT_MIN = 1.5;
// 运动上限（峰值；进退场 ease 的峰值系数 1.25 已经算在时长里）
const YAW_LIMIT = Math.PI / 2; // 90°/s
const PITCH_LIMIT = Math.PI / 3; // 60°/s
const LOGD_LIMIT = 1.2; // |d(ln d)/dt|
const TARGET_LIMIT = 1.5; // |Q̇| / d
const CAMERA_LIMIT = 3; // |Ċ| / d
/** 一组运动上限（弧度/秒、1/秒） */
interface Limits {
  yaw: number;
  pitch: number;
  logd: number;
  target: number;
  camera: number;
}
const COMFORT: Limits = { yaw: YAW_LIMIT, pitch: PITCH_LIMIT, logd: LOGD_LIMIT, target: TARGET_LIMIT, camera: CAMERA_LIMIT };
/**
 * 「收」（用户自己轻触、按 Esc）的上限比自动播放的宽一些：人有预期，可以快，但不能晃。
 * 照舒适上限收，从跟拍位拉回全景（距离约 15 → 92）光缩放就要将近 3 秒，跳过却还要等，显得拖；
 * 照设计原来的 0.6 秒收，转 97° 实测峰值 510°/s。这组让回程落在 1.5–1.8 秒左右
 */
export const GLIMPSE_RETURN_LIMITS: Limits = { yaw: (2 * Math.PI) / 3, pitch: (4 * Math.PI) / 9, logd: 2, target: 2.5, camera: 5 };
/** 回程是五次 Hermite：纯位移时峰值速度是平均的 1.875 倍，估起始时长用它，再逐点检查 */
const HERMITE_PEAK = 1.875;
/** 弧上转向峰值的节奏目标（35°/s）；YAW_LIMIT 是硬上限 */
const TURN_SOFT = (35 * Math.PI) / 180;
// 镜头：侧后方跟拍
/** 注视点在路线上光标前方这么远 */
const AHEAD = 3.5;
/** 镜头在路线后方、侧面、上方这么远；三个数乘 scale（竖屏放大机位，aspect<1 时 min(1/aspect, SCALE_MAX)） */
const BACK = 7;
const SIDE = 9;
const UP = 5.5;
const SCALE_MAX = 1.5;
/** 百天的弧跨这么多天 */
const ARC_DAYS = 99;
/** 弧长表按天数均匀取这么多个样点（够密，插值的折角看不出来） */
const ARC_SAMPLES = 2000;
/** 弧上取前方朝向的差分步长（天）：解析曲线光滑，差分不会有折角 */
const DIR_X = 0.01;
/** 只有一站的生日：路线从它往里这么多单位起飞，起飞那一拍不写字 */
const SOLO_LEAD = 10;
/** 「收」的回程时长下限 */
const END_MIN = 0.6;
/** 「收」的起点速度、加速度：用 t、t−h、t−2h 三点差分，h 这么长（秒） */
const END_H = 1 / 240;
/** 字提前到站 0.2 秒淡入、离站后 0.3 秒淡出 */
const LABEL_IN = 0.2;
const LABEL_OUT = 0.3;

const NUMERALS = '一两三四五六七八九十';
const TAU = Math.PI * 2;

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const smooth = (k: number) => k * k * (3 - 2 * k);

// ── 缓动（任务书 §1）──
/** smootherstep：两端一阶二阶导数为 0，峰值 1.875 */
const S = (x: number) => x * x * x * (10 - 15 * x + 6 * x * x);
/** S 的导数 */
const B = (x: number) => 30 * x * x * (1 - x) * (1 - x);
/** ∫₀ˣ S，I(1) = 0.5 */
const I = (x: number) => x ** 4 * (2.5 - 3 * x + x * x);
/** 进退场用「平滑升速—匀速—平滑降速」，α = 0.2，峰值系数 EASE_PEAK（C³） */
function ease(x: number): number {
  x = clamp(x, 0, 1);
  const a = 0.2,
    c = 1 / (1 - a);
  if (x < a) return c * a * I(x / a);
  if (x <= 1 - a) return c * (x - a / 2);
  return 1 - c * a * I((1 - x) / a);
}
const EASE_PEAK = 1.25;

/** 一站：u 是它在路线上的位置（0–1）；label 为 false 的只闪不写字（百天途中有照片的日子） */
export interface GlimpseStop {
  date: LocalDate;
  u: number;
  main: string;
  sub: string;
  label: boolean;
}

/** 百天的弧：解析曲线 + 弧长表；path 只是光带画的折线（glimpsefx 用），镜头、光标走解析曲线 */
export interface GlimpseArc {
  path: TrailPath;
  s0: number;
  s1: number;
  /** 起点（today − days）的角度、距 since 的天数、弧跨的天数 */
  a0: number;
  n0: number;
  days: number;
  /** 均匀天数样点的累计弧长（ARC_SAMPLES + 1 个）：u → s → x 靠它反查 */
  ss: Float32Array;
  /** 每个整数天的 u = s(x)/L（days + 1 个）：只闪的站按它放 */
  us: Float32Array;
  /** 转向曲率峰值 |dβ/ds|（弧度/单位）：弧的 fly 时长按它算 */
  kappa: number;
}

/** 路线：射线（周年、生日）或螺线弧（百天）；color 决定光带、字的颜色 */
export interface GlimpseRoute {
  kind: 'ray' | 'arc';
  color: 'gold' | 'rose';
  stops: GlimpseStop[]; // 按 u 排好；第一站 u = 0，最后一站 u = 1（百天的弧也是：起点、终点各一站）
  length: number; // 路线长度（世界单位）：时间轴按它算飞多久
  ray?: { a: number; r0: number; r1: number }; // 射线的角和半径区间
  arc?: GlimpseArc;
}

export interface GlimpseOptions {
  kind: 'years' | 'birthday' | 'days';
  since: LocalDate;
  today: LocalDate;
  /** 生日那档：谁的生日（'MM-DD'、名字、是不是自己、两人同一天） */
  birthday?: { monthDay: string; name: string; mine: boolean; both: boolean };
  /** 那天回忆的标题（没有返回 null）：写在日期后面 */
  titleOf: (date: LocalDate) => string | null;
  /** 那天还有没有照片：百天那档途中有照片的日子要闪一下 */
  hasPhotos: (date: LocalDate) => boolean;
}

/** 镜头姿态：位置 + 注视点 */
export interface GlimpsePose {
  pos: Vec3;
  target: Vec3;
}

/**
 * 一段：in 进场、dwell 停站、fly 飞、final 停终点、out 退场；stop 是停的站（别的段是要去的那站，
 * in 是 0，out 是 -1）。u0/u1 是段起止的 u；dwell 的 u1 是停拍推完的位置，fly 的 u0 从上一站推完的地方起。
 */
export interface GlimpseLeg {
  kind: 'in' | 'dwell' | 'fly' | 'final' | 'out';
  t0: number;
  t1: number;
  u0: number;
  u1: number;
  stop: number;
  /** 进场后的第一站停拍：s 从 0 平滑升到 VD，不是全程 VD */
  first?: boolean;
}

/** 整趟：时间轴、选的那一侧、总时长 */
export interface GlimpsePlan {
  route: GlimpseRoute;
  legs: GlimpseLeg[];
  total: number;
  side: 1 | -1;
  scale: number;
}

/**
 * 机位 (Q, ψ, θ, d)：注视点、水平朝向（从镜头看向注视点）、仰角、镜头到注视点的距离。
 * r = d·cos θ、h = d·sin θ、pos = Q − (sin ψ, 0, cos ψ)·r + (0, h, 0)。
 */
interface Rig4 {
  x: number;
  y: number;
  z: number;
  yaw: number;
  pitch: number;
  d: number;
}
const rig4 = (): Rig4 => ({ x: 0, y: 0, z: 0, yaw: 0, pitch: 0, d: 0 });
const P: Vec3 = { x: 0, y: 0, z: 0 },
  F: Vec3 = { x: 0, y: 0, z: 0 },
  A0: Vec3 = { x: 0, y: 0, z: 0 },
  A1: Vec3 = { x: 0, y: 0, z: 0 };
const BASE = rig4(),
  OTHER = rig4(),
  R0 = rig4(),
  R1 = rig4(),
  RT = rig4();

/** a 转到 b 最近的那个角度差，(−π, π] */
function angleDiff(a: number, b: number): number {
  let d = (b - a) % (2 * Math.PI);
  if (d > Math.PI) d -= 2 * Math.PI;
  else if (d <= -Math.PI) d += 2 * Math.PI;
  return d;
}

/** 日期 + 回忆标题（有的话）：「2024.05.20 · 厦门」 */
const subOf = (date: LocalDate, titleOf: GlimpseOptions['titleOf']): string => {
  const t = titleOf(date);
  return t ? `${dotted(date)} · ${t}` : dotted(date);
};

/** 整周年：射线，站是第 0..N 年的这一天（在一起满了几年 N，按今天算、至少 1，同 semantic.ts 的周年标签算法） */
function yearsRoute(o: GlimpseOptions): GlimpseRoute {
  const s = parseLocalDate(o.since);
  const anniv = (k: number) => formatLocalDate(s.y + k, s.m, Math.min(s.d, daysInMonth(s.y + k, s.m)));
  // 强放调试、今天不是周年时，路线走到最近一个周年为止（不用 occasion.n）
  let n = 1;
  while (anniv(n + 1) <= o.today) n++;
  const a = dayAngle(o.since),
    r0 = dayRadius(o.since, o.since),
    r1 = dayRadius(anniv(n), o.since);
  const stops: GlimpseStop[] = [];
  for (let k = 0; k <= n; k++) {
    const date = anniv(k),
      last = k === n;
    stops.push({
      date,
      u: (dayRadius(date, o.since) - r0) / (r1 - r0),
      main: last && date === o.today ? '今天' : k === 0 ? '在一起' : k <= 10 ? `${NUMERALS.charAt(k - 1)}周年` : `${k} 周年`,
      sub: subOf(date, o.titleOf),
      label: true,
    });
  }
  return { kind: 'ray', color: 'gold', stops, length: r1 - r0, ray: { a, r0, r1 } };
}

/** 生日：那个人的生日射线；站是落在 [since, today] 里的每一个生日 */
function birthdayRoute(o: GlimpseOptions): GlimpseRoute | null {
  const b = o.birthday;
  if (!b || !isMonthDay(b.monthDay)) return null;
  const s = parseLocalDate(o.since);
  // 和 semantic.ts 的生日射线同一个角：那一年生日所在的方向
  const a = dayAngle(monthDayIn(b.monthDay, s.y));
  const days: LocalDate[] = [];
  for (let y = s.y; ; y++) {
    const d = monthDayIn(b.monthDay, y);
    if (d > o.today) break;
    if (d >= o.since) days.push(d);
  }
  if (!days.length) return null;
  const main = b.both ? '你们俩的生日' : b.mine ? '我的生日' : `${b.name} 的生日`;
  const last = days[days.length - 1]!,
    r1 = dayRadius(last, o.since);
  // 只有一站（今年是第一个生日）：路线从它往里 SOLO_LEAD 个单位起飞，起飞点放一个只闪不写字的站
  const r0 = days.length > 1 ? dayRadius(days[0]!, o.since) : Math.max(1.5, r1 - SOLO_LEAD);
  const stops: GlimpseStop[] = [];
  if (days.length === 1) stops.push({ date: last, u: 0, main: '', sub: '', label: false });
  for (const d of days)
    stops.push({
      date: d,
      u: (dayRadius(d, o.since) - r0) / (r1 - r0),
      main: d === last && d === o.today ? '今天' : main,
      sub: subOf(d, o.titleOf),
      label: true,
    });
  return { kind: 'ray', color: 'rose', stops, length: r1 - r0, ray: { a, r0, r1 } };
}

/** 解析弧上 x（从起点起的天数，可以是小数）处的点：角度 365.25 天一圈、半径按 yearRadius，和 dayAnchor 同一个式子 */
function arcPoint(arc: GlimpseArc, x: number, out: Vec3): Vec3 {
  const a = arc.a0 + (TAU * x) / 365.25,
    r = yearRadius((arc.n0 + x) / 365.25);
  out.x = Math.sin(a) * r;
  out.z = -Math.cos(a) * r;
  out.y = Math.sin(a * 2 + r * 0.045) * SPIRAL.warp;
  return out;
}

/** s(x)：均匀样点的弧长表上线性插 */
function arcS(arc: GlimpseArc, x: number): number {
  const ss = arc.ss,
    n = ss.length - 1,
    i = clamp((x / arc.days) * n, 0, n),
    i0 = Math.min(n - 1, Math.floor(i)),
    f = i - i0;
  return ss[i0]! * (1 - f) + ss[i0 + 1]! * f;
}

/** x(s)：弧长反查天数，二分加线性插值 */
function arcX(arc: GlimpseArc, s: number): number {
  const ss = arc.ss,
    n = ss.length - 1;
  s = clamp(s, 0, ss[n]!);
  let lo = 0,
    hi = n;
  while (lo + 1 < hi) {
    const mid = (lo + hi) >> 1;
    if (ss[mid]! <= s) lo = mid;
    else hi = mid;
  }
  const f = (s - ss[lo]!) / Math.max(1e-9, ss[lo + 1]! - ss[lo]!);
  return ((lo + clamp(f, 0, 1)) / n) * arc.days;
}

/** 解析弧上 x 处前方 f̂ 的水平角（±DIR_X 天差分）：曲线光滑，不会有折角 */
function arcBeta(arc: GlimpseArc, x: number): number {
  arcPoint(arc, x - DIR_X, A0);
  arcPoint(arc, x + DIR_X, A1);
  return Math.atan2(A1.x - A0.x, A1.z - A0.z);
}

/** 满 100 天：螺线的解析式（最近 100 天，连续天数 x ∈ [0, 99]），途中有照片的日子是只闪的站 */
function daysRoute(o: GlimpseOptions): GlimpseRoute {
  const start = addDays(o.today, -ARC_DAYS);
  // 光带还是画每天锚点连的折线（glimpsefx 只认它）；镜头、光标走解析曲线
  const path = trailPath(start, o.today, o.today, o.since);
  const arc: GlimpseArc = {
    path,
    s0: 0,
    s1: path.total,
    a0: dayAngle(start),
    n0: daysBetween(o.since, start),
    days: ARC_DAYS,
    ss: new Float32Array(ARC_SAMPLES + 1),
    us: new Float32Array(ARC_DAYS + 1),
    kappa: 0,
  };
  arcPoint(arc, 0, P);
  for (let i = 1; i <= ARC_SAMPLES; i++) {
    arcPoint(arc, (ARC_DAYS * i) / ARC_SAMPLES, A0);
    arc.ss[i] = arc.ss[i - 1]! + Math.hypot(A0.x - P.x, A0.y - P.y, A0.z - P.z);
    P.x = A0.x;
    P.y = A0.y;
    P.z = A0.z;
  }
  const L = Math.max(1e-6, arc.ss[ARC_SAMPLES]!);
  for (let k = 0; k <= ARC_DAYS; k++) arc.us[k] = arcS(arc, k) / L;
  // 转向曲率峰值 |dβ/ds|：相邻样点 f̂ 的水平角差 / 弧长差
  let bPrev = 0;
  for (let i = 0; i <= ARC_SAMPLES; i++) {
    const beta = arcBeta(arc, (ARC_DAYS * i) / ARC_SAMPLES);
    if (i > 0) arc.kappa = Math.max(arc.kappa, Math.abs(angleDiff(bPrev, beta)) / Math.max(1e-9, arc.ss[i]! - arc.ss[i - 1]!));
    bPrev = beta;
  }
  const stops: GlimpseStop[] = [{ date: start, u: 0, main: '最近 100 天', sub: subOf(start, o.titleOf), label: true }];
  for (let k = 1; k < ARC_DAYS; k++) {
    const d = addDays(start, k);
    if (o.hasPhotos(d)) stops.push({ date: d, u: arc.us[k]!, main: '', sub: '', label: false });
  }
  stops.push({ date: o.today, u: 1, main: '今天', sub: subOf(o.today, o.titleOf), label: true });
  return { kind: 'arc', color: 'gold', stops, length: L, arc };
}

/** 建路线；建不出来（比如生日那档缺月日）返回 null */
export function glimpseRoute(o: GlimpseOptions): GlimpseRoute | null {
  if (o.kind === 'birthday') return birthdayRoute(o);
  if (o.kind === 'days') return daysRoute(o);
  return yearsRoute(o);
}

/** 路线上 u 处的点：射线按 semantic.ts rayAt 同一个公式；弧是 u → s → x 的解析曲线 */
export function routePoint(route: GlimpseRoute, u: number, out: Vec3): Vec3 {
  u = clamp(u, 0, 1);
  if (route.kind === 'ray') {
    const { a, r0, r1 } = route.ray!;
    const r = r0 + (r1 - r0) * u;
    out.x = Math.sin(a) * r;
    out.z = -Math.cos(a) * r;
    out.y = Math.sin(a * 2 + r * 0.045) * SPIRAL.warp;
    return out;
  }
  const arc = route.arc!;
  return arcPoint(arc, arcX(arc, u * route.length), out);
}

/** 路线上 u 处的水平前方（单位向量）：射线是常量；弧用 ±0.01 天的解析差分。镜头、光标的朝向都用它 */
function routeDir(route: GlimpseRoute, u: number, out: Vec3): Vec3 {
  if (route.kind === 'ray') {
    const a = route.ray!.a;
    out.x = Math.sin(a);
    out.y = 0;
    out.z = -Math.cos(a);
    return out;
  }
  const arc = route.arc!,
    x = arcX(arc, clamp(u, 0, 1) * route.length);
  arcPoint(arc, x - DIR_X, A0);
  arcPoint(arc, x + DIR_X, A1);
  let dx = A1.x - A0.x,
    dz = A1.z - A0.z;
  const l = Math.hypot(dx, dz);
  if (l < 1e-6) {
    // 理论上到不了（解析曲线一直往前走）：朝螺线切线（角度增大的方向）兜底
    arcPoint(arc, x, A0);
    const rl = Math.hypot(A0.x, A0.z) || 1;
    dx = -A0.z / rl;
    dz = A0.x / rl;
  } else {
    dx /= l;
    dz /= l;
  }
  out.x = dx;
  out.y = 0;
  out.z = dz;
  return out;
}

/** 飞行机位：注视点在光标前方 AHEAD，镜头在光标后方 BACK、侧面 SIDE、上方 UP（后三个乘 scale） */
function flightRig4(route: GlimpseRoute, u: number, side: 1 | -1, scale: number, o: Rig4): Rig4 {
  routePoint(route, u, P);
  routeDir(route, u, F);
  // ŝ = f̂ 绕 y 转 90°：(x,z) → (−z,x)；side 挑左右
  const sx = -F.z * side,
    sz = F.x * side,
    px = P.x - F.x * BACK * scale + sx * SIDE * scale,
    pz = P.z - F.z * BACK * scale + sz * SIDE * scale,
    py = P.y + UP * scale;
  o.x = P.x + F.x * AHEAD;
  o.y = P.y;
  o.z = P.z + F.z * AHEAD;
  const r = Math.max(1e-3, Math.hypot(o.x - px, o.z - pz));
  o.yaw = Math.atan2(o.x - px, o.z - pz);
  o.pitch = Math.atan2(py - o.y, r);
  o.d = Math.hypot(r, py - o.y);
  return o;
}

/** 终点段的机位：跟拍位 + 镜头和注视点一起沿终点的前方 f̂ 前移 delta（δ(t) = PUSH_LEN·S(t/T)） */
function finalRig(route: GlimpseRoute, side: 1 | -1, scale: number, delta: number, o: Rig4): Rig4 {
  flightRig4(route, 1, side, scale, o);
  routeDir(route, 1, F);
  o.x += F.x * delta;
  o.z += F.z * delta;
  return o;
}

/** 镜头姿态 → 机位 */
function poseRig4(from: GlimpsePose, o: Rig4): Rig4 {
  const dx = from.target.x - from.pos.x,
    dy = from.pos.y - from.target.y,
    dz = from.target.z - from.pos.z,
    r = Math.max(1e-3, Math.hypot(dx, dz));
  o.x = from.target.x;
  o.y = from.target.y;
  o.z = from.target.z;
  o.yaw = Math.atan2(dx, dz);
  o.pitch = Math.atan2(dy, r);
  o.d = Math.hypot(r, dy);
  return o;
}

/** 机位 → 镜头姿态：r = d·cos θ、h = d·sin θ、pos = Q − (sin ψ, 0, cos ψ)·r + (0, h, 0) */
function rigToPose(o: Rig4, out: GlimpsePose): void {
  const r = o.d * Math.cos(o.pitch),
    h = o.d * Math.sin(o.pitch);
  out.target.x = o.x;
  out.target.y = o.y;
  out.target.z = o.z;
  out.pos.x = o.x - Math.sin(o.yaw) * r;
  out.pos.y = o.y + h;
  out.pos.z = o.z - Math.cos(o.yaw) * r;
}

/**
 * 机位 a → b（e = ease(t/T)，任务书 §3）：距离对数插、仰角线性插、朝向走最近的角度差；
 * 注视点按距离加权推进 F(e)——远的时候多挪、近的时候少挪。
 */
function mixRig4(a: Rig4, b: Rig4, e: number, o: Rig4): Rig4 {
  const k = Math.log(b.d / a.d);
  o.d = a.d * Math.exp(k * e);
  o.pitch = a.pitch + (b.pitch - a.pitch) * e;
  o.yaw = a.yaw + angleDiff(a.yaw, b.yaw) * e;
  const f = Math.abs(k) < 1e-6 ? e : (Math.exp(k * e) - 1) / (Math.exp(k) - 1);
  o.x = a.x + (b.x - a.x) * f;
  o.y = a.y + (b.y - a.y) * f;
  o.z = a.z + (b.z - a.z) * f;
  return o;
}

/**
 * a → b 转场要的最短时长（任务书 §4）：峰值（ease 峰值 1.25 × 平均）不越过任一项运动限速，
 * 取所有下限里最大的那个，不设上限——上限会把限速破掉。
 */
function transitionDuration(a: Rig4, b: Rig4, minT: number, lim: Limits = COMFORT, peak = EASE_PEAK): number {
  const dpsi = Math.abs(angleDiff(a.yaw, b.yaw)),
    dth = Math.abs(b.pitch - a.pitch),
    k = Math.log(b.d / a.d);
  const Z = Math.abs(k) < 1e-6 ? a.d : (b.d - a.d) / k; // 距离的对数平均
  const q = Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z) / Z;
  const cosMax = Math.max(Math.cos(a.pitch), Math.cos(b.pitch));
  return Math.max(
    minT,
    (peak * dpsi) / lim.yaw,
    (peak * dth) / lim.pitch,
    (peak * Math.abs(k)) / lim.logd,
    (peak * q) / lim.target,
    (peak * (q + Math.hypot(k, dth, cosMax * dpsi))) / lim.camera,
  );
}

/**
 * 带下限的水位法（任务书 §6）：比例 λ 统一缩放想要的时长，压到下限的段固定在下限、退出缩放，
 * 剩下的重新算 λ，直到没有新的段触底。下限加起来就超过预算时不再压（全取下限，总长超了也认）。
 */
function compress(desired: number[], lower: number[], budget: number): number[] {
  const sum = (a: number[]) => a.reduce((v, x) => v + x, 0);
  const d = desired.map((v, i) => Math.max(v, lower[i]!));
  if (sum(d) <= budget || sum(lower) >= budget) return sum(lower) >= budget ? lower.slice() : d;
  const out = d.slice();
  let active = d.map((_, i) => i),
    remaining = budget;
  while (active.length) {
    const f = remaining / active.reduce((v, i) => v + d[i]!, 0);
    const hit = active.filter((i) => f * d[i]! < lower[i]!);
    if (!hit.length) {
      for (const i of active) out[i] = f * d[i]!;
      break;
    }
    for (const i of hit) {
      out[i] = lower[i]!;
      remaining -= lower[i]!;
    }
    active = active.filter((i) => !hit.includes(i));
  }
  return out;
}

/** 跟拍机位的 d（注视点到镜头的距离）：AHEAD+BACK、SIDE、UP 合成的斜距，约 14.9 × scale */
const dFollow = (scale: number) => Math.hypot(AHEAD + BACK * scale, SIDE * scale, UP * scale);

/**
 * fly 的时长下限（任务书 §5）：跟拍构图是刚性的，注视点、镜头都以路线速度 ṡ 平移，要 ṡ ≤ V。
 * 两端 VD 的 fly 峰值速度 1.875·ℓ/T − 0.875·VD；最后一段（v1 = 0）用保守上界 max(v0,v1) + 1.875·A。
 */
function flyFloor(ell: number, last: boolean, V: number): number {
  const bound = last ? (1.875 * ell) / (V - VD / 16) : (15 * ell) / (8 * V + 7 * VD);
  return Math.max(FLY_FLOOR, bound);
}

/** 按出发的镜头（选侧、算进退场时长）和宽高比（竖屏放大机位）排好时间轴 */
export function glimpsePlan(route: GlimpseRoute, from: GlimpsePose, aspect: number): GlimpsePlan {
  const scale = clamp(1 / Math.min(1, aspect), 1, SCALE_MAX);
  const fromRig = poseRig4(from, OTHER);
  const stops = route.stops;
  // 选侧：射线照出发朝向差得少的那侧（出口朝向和入口一样，不用另算）；
  // 弧两侧各算完整的进、退场时长，依次比 max(T_in, T_out)、T_in + T_out、入口转角，平局取 +1
  let side: 1 | -1;
  if (route.kind === 'ray') {
    const y0 = flightRig4(route, stops[0]!.u, 1, scale, R0).yaw,
      y1 = flightRig4(route, stops[0]!.u, -1, scale, R1).yaw;
    side = Math.abs(angleDiff(fromRig.yaw, y0)) <= Math.abs(angleDiff(fromRig.yaw, y1)) ? 1 : -1;
  } else {
    const keys = (s: 1 | -1): [number, number, number] => {
      flightRig4(route, stops[0]!.u, s, scale, R0);
      finalRig(route, s, scale, PUSH_LEN, R1);
      const tin = transitionDuration(fromRig, R0, IN_MIN),
        tout = transitionDuration(R1, fromRig, OUT_MIN);
      return [Math.max(tin, tout), tin + tout, Math.abs(angleDiff(fromRig.yaw, R0.yaw))];
    };
    const p = keys(1),
      m = keys(-1);
    side = p[0] !== m[0] ? (p[0] < m[0] ? 1 : -1) : p[1] !== m[1] ? (p[1] < m[1] ? 1 : -1) : p[2] <= m[2] ? 1 : -1;
  }
  const inDur = transitionDuration(fromRig, flightRig4(route, stops[0]!.u, side, scale, R0), IN_MIN);
  const outDur = transitionDuration(finalRig(route, side, scale, PUSH_LEN, R0), fromRig, OUT_MIN);

  // 有停拍的站：第一站（哪怕不写字）、所有写字的中间站、最后一站；只闪的站不停
  const legStops: number[] = [0];
  for (let i = 1; i < stops.length - 1; i++) if (stops[i]!.label) legStops.push(i);
  if (stops.length > 1) legStops.push(stops.length - 1);

  const L = Math.max(1e-6, route.length),
    V = TARGET_LIMIT * dFollow(scale);
  const legs: GlimpseLeg[] = [{ kind: 'in', t0: 0, t1: inDur, u0: stops[0]!.u, u1: stops[0]!.u, stop: 0 }];
  const pack: { leg: number; want: number; low: number }[] = [];
  // fly 从上一站 dwell 推完的地方起飞：第一遍先按想要的时长推（压完再按真实时长重算一遍 u）
  let prevU = stops[0]!.u;
  for (let k = 0; k < legStops.length; k++) {
    const i = legStops[k]!,
      st = stops[i]!,
      last = i === stops.length - 1;
    if (k > 0) {
      const ell = (st.u - prevU) * L;
      let want = clamp(ell / FLY_V, FLY_MIN, FLY_MAX),
        low = flyFloor(ell, last, V);
      if (route.kind === 'arc') {
        // 转向峰值 ≤ ω 的最短时长：κ·(VD/16 + 1.875·ℓ/T) ≤ ω 解出 T（最后一段 v1 = 0 的上界）
        const den35 = TURN_SOFT / route.arc!.kappa - VD / 16,
          den90 = YAW_LIMIT / route.arc!.kappa - VD / 16;
        want = clamp(Math.max(ell / FLY_V, den35 > 0 ? (1.875 * ell) / den35 : 0), ARC_MIN, ARC_MAX);
        low = Math.max(low, den90 > 0 ? (1.875 * ell) / den90 : 0);
      }
      pack.push({ leg: legs.length, want, low });
      legs.push({ kind: 'fly', t0: 0, t1: 0, u0: prevU, u1: st.u, stop: i });
    }
    if (last) {
      legs.push({ kind: 'final', t0: 0, t1: FINAL_S, u0: st.u, u1: st.u, stop: i });
      prevU = st.u;
    } else {
      const first = k === 0;
      pack.push({ leg: legs.length, want: DWELL_S, low: DWELL_FLOOR });
      legs.push({ kind: 'dwell', t0: 0, t1: 0, u0: st.u, u1: st.u, stop: i, first });
      prevU = st.u + (VD * (DWELL_S - (first ? RAMP_S / 2 : 0))) / L;
    }
  }
  legs.push({ kind: 'out', t0: 0, t1: outDur, u0: prevU, u1: prevU, stop: -1 });

  // 15 秒是软目标：预算给可压的段，水位法带下限；下限加起来都超就全取下限（总长超了也认）
  const durs = compress(
    pack.map((p) => p.want),
    pack.map((p) => p.low),
    Math.min(PACK_S, TOTAL_SOFT - inDur - outDur - FINAL_S),
  );
  for (let j = 0; j < pack.length; j++) legs[pack[j]!.leg]!.t1 = durs[j]!;

  // 时长定了再定 u（此刻 t1 存的还是时长）：dwell 的 u1 是推完的位置，fly 的 u0 从上一站推完的地方起
  let curU = stops[0]!.u;
  for (let j = 0; j < legs.length; j++) {
    const l = legs[j]!,
      T = l.t1;
    if (l.kind === 'fly') {
      l.u0 = curU;
      l.u1 = stops[l.stop]!.u;
      const ell = (l.u1 - l.u0) * L,
        cap = (2 * ell) / (VD + (legs[j + 1]?.kind === 'final' ? 0 : VD));
      // A = ℓ/T − (v0+v1)/2 ≥ 0 要求 T ≤ 2ℓ/(v0+v1)；不满足时把 T 缩到刚好满足
      if (ell > 0 && T > cap) l.t1 = cap;
      curU = l.u1;
    } else if (l.kind === 'dwell') {
      l.u0 = stops[l.stop]!.u;
      const tau = l.first ? Math.min(RAMP_S, T / 2) : 0;
      l.u1 = l.u0 + (VD * (T - tau / 2)) / L;
      curU = l.u1;
    } else if (l.kind === 'final') {
      l.u0 = l.u1 = stops[l.stop]!.u;
      curU = l.u1;
    }
  }
  let t = 0;
  for (const l of legs) {
    const d = l.t1;
    l.t0 = t;
    l.t1 = t + d;
    t = l.t1;
  }
  return { route, legs, total: t, side, scale };
}

/** t 时刻在路线的哪（u）、第几段 */
export function glimpseAt(plan: GlimpsePlan, t: number): { u: number; leg: number } {
  t = clamp(t, 0, plan.total);
  const legs = plan.legs;
  let leg = legs.length - 1;
  for (let i = 0; i < legs.length; i++)
    if (t < legs[i]!.t1) {
      leg = i;
      break;
    }
  const l = legs[leg]!,
    L = Math.max(1e-6, plan.route.length),
    T = l.t1 - l.t0,
    dt = t - l.t0;
  let u: number;
  if (l.kind === 'dwell') {
    // 停站用恒定速度 VD 微推；第一站先进场 s 不动，用 τ 秒平滑升到 VD（s = s0 + VD·τ·I(t/τ)）
    const tau = l.first ? Math.min(RAMP_S, T / 2) : 0,
      push = tau > 0 && dt <= tau ? VD * tau * I(clamp(dt / tau, 0, 1)) : VD * (dt - tau / 2);
    u = l.u0 + push / L;
  } else if (l.kind === 'fly') {
    // s = s0 + T·[v0·x + (v1−v0)·I(x) + A·S(x)]：两端速度给定，smootherstep 补差，接缝 C²
    const x = T > 0 ? clamp(dt / T, 0, 1) : 1,
      v1 = legs[leg + 1]?.kind === 'final' ? 0 : VD,
      A = ((l.u1 - l.u0) * L) / T - (VD + v1) / 2;
    u = l.u0 + (T * (VD * x + (v1 - VD) * I(x) + A * S(x))) / L;
  } else {
    u = l.u0;
  }
  return { u: clamp(u, 0, 1), leg };
}

/** 各站的字现在该有多亮（0–1）：到站前 0.2 秒淡入、离站后 0.3 秒淡出；终点不淡出，留到退场随整场淡掉 */
export function glimpseLabels(plan: GlimpsePlan, t: number): { stop: number; k: number }[] {
  const out: { stop: number; k: number }[] = [];
  for (const l of plan.legs) {
    if (l.kind !== 'dwell' && l.kind !== 'final') continue;
    const st = plan.route.stops[l.stop];
    if (!st || !st.label) continue;
    const inK = smooth(clamp((t - (l.t0 - LABEL_IN)) / LABEL_IN, 0, 1)),
      outK = l.kind === 'final' ? 1 : 1 - smooth(clamp((t - l.t1) / LABEL_OUT, 0, 1)),
      k = Math.min(inK, outK);
    if (k > 0.01) out.push({ stop: l.stop, k });
  }
  // 同时最多两个：取最亮的两个（到站前一站和上一站收尾时最多重叠两个）
  out.sort((a, b) => b.k - a.k);
  return out.slice(0, 2).sort((a, b) => a.stop - b.stop);
}

/** t 时刻的机位写进 o：in/out 在机位之间插（§3），其余段是跟拍位；glimpseCamera、glimpseReturn 共用同一条路径 */
function rigAt(plan: GlimpsePlan, from: GlimpsePose, t: number, o: Rig4): Rig4 {
  const { u, leg } = glimpseAt(plan, t),
    l = plan.legs[leg]!,
    route = plan.route,
    T = Math.max(1e-9, l.t1 - l.t0);
  if (l.kind === 'in') {
    mixRig4(poseRig4(from, OTHER), flightRig4(route, l.u1, plan.side, plan.scale, R0), ease((t - l.t0) / T), o);
  } else if (l.kind === 'out') {
    mixRig4(finalRig(route, plan.side, plan.scale, PUSH_LEN, R0), poseRig4(from, OTHER), ease((t - l.t0) / T), o);
  } else if (l.kind === 'final') {
    // 路线参数停在 u = 1：镜头和注视点一起沿终点的前方 f̂ 前移 δ(t)
    finalRig(route, plan.side, plan.scale, PUSH_LEN * S(clamp((t - l.t0) / T, 0, 1)), o);
  } else {
    flightRig4(route, u, plan.side, plan.scale, o);
  }
  return o;
}

/** t 时刻的镜头写进 out；返回 u 和第几段，galaxy.ts 用来推光带、光标、到站 */
export function glimpseCamera(plan: GlimpsePlan, from: GlimpsePose, t: number, out: GlimpsePose): { u: number; leg: number } {
  const legs = plan.legs;
  if (t <= 0 || t >= plan.total) {
    // 两头分毫不差地是出发的镜头（不经三角函数重建）
    out.pos.x = from.pos.x;
    out.pos.y = from.pos.y;
    out.pos.z = from.pos.z;
    out.target.x = from.target.x;
    out.target.y = from.target.y;
    out.target.z = from.target.z;
    return t <= 0 ? { u: legs[0]!.u0, leg: 0 } : { u: legs[legs.length - 1]!.u1, leg: legs.length - 1 };
  }
  const at = glimpseAt(plan, t);
  rigToPose(rigAt(plan, from, t, BASE), out);
  return at;
}

/**
 * 「收」的回程（任务书 §9）：六个坐标（Qx, Qy, Qz、展开成连续角的 ψ、θ、ln d）从 t 时刻的机位
 * （含三点差分出的速度、加速度）沿五次 Hermite 平滑接回出发机位；终点速度、加速度为 0。
 */
export interface GlimpseReturn {
  /** 起点各坐标 */
  q0: number[];
  /** ΔQ = q1 − q0（终点是出发机位，ψ 展开到 q0 附近） */
  dq: number[];
  /** 起点速度、加速度（已乘 T、T²） */
  v: number[];
  a: number[];
  dur: number;
}

const END_COORDS = 6;
/** 回程峰值检查的取样段数 */
const END_SAMPLES = 60;

export function glimpseReturn(plan: GlimpsePlan, from: GlimpsePose, t: number): GlimpseReturn {
  // 把 tt 时刻的机位抓成六个坐标；ψ 展开到 ref 附近，差分不撞 ±π
  const grab = (tt: number, ref: number, out: number[]): void => {
    rigAt(plan, from, tt, RT);
    out[0] = RT.x;
    out[1] = RT.y;
    out[2] = RT.z;
    out[3] = ref + angleDiff(ref, RT.yaw);
    out[4] = RT.pitch;
    out[5] = Math.log(Math.max(1e-9, RT.d));
  };
  const q0 = new Array<number>(END_COORDS),
    q_ = new Array<number>(END_COORDS),
    q__ = new Array<number>(END_COORDS),
    q1 = new Array<number>(END_COORDS),
    v0 = new Array<number>(END_COORDS).fill(0),
    a0 = new Array<number>(END_COORDS).fill(0);
  rigAt(plan, from, t, RT);
  grab(t, RT.yaw, q0);
  // 起点速度：t、t−h、t−2h 三点差分；t < 2h 时取 0。
  // 加速度不接（a0 留 0，接缝只到一阶连续，肉眼看不出）：飞行段的速度本来就贴着上限，轻触时要是正在加速，
  // 把加速度也接上会让回程开头再冲一小下、超过上限，而且这一下和回程多长无关，放长时长也压不回去
  if (t >= 2 * END_H) {
    grab(t - END_H, q0[3]!, q_);
    grab(t - 2 * END_H, q0[3]!, q__);
    for (let j = 0; j < END_COORDS; j++) v0[j] = (3 * q0[j]! - 4 * q_[j]! + q__[j]!) / (2 * END_H);
  }
  // 出发机位放 R1：rigAt 的 in/out 分支会把 R0 当草稿覆写
  poseRig4(from, R1);
  q1[0] = R1.x;
  q1[1] = R1.y;
  q1[2] = R1.z;
  q1[3] = q0[3]! + angleDiff(q0[3]!, R1.yaw);
  q1[4] = R1.pitch;
  q1[5] = Math.log(Math.max(1e-9, R1.d));
  const dq = q1.map((v, j) => v - q0[j]!);
  // 时长：先按 transitionDuration（当时机位 → 出发机位），再沿回程取样查峰值，超限就放宽 25%
  const L = GLIMPSE_RETURN_LIMITS;
  let T = Math.max(END_MIN, transitionDuration(rigAt(plan, from, t, RT), R1, END_MIN, L, HERMITE_PEAK));
  const c = new Array<number>(END_COORDS),
    cp = new Array<number>(END_COORDS);
  // 六个坐标还原成镜头位置：查「镜头每秒挪多少个距离」那一项用
  const camX = (q: number[]) => q[0]! - Math.sin(q[3]!) * Math.exp(q[5]!) * Math.cos(q[4]!),
    camY = (q: number[]) => q[1]! + Math.exp(q[5]!) * Math.sin(q[4]!),
    camZ = (q: number[]) => q[2]! - Math.cos(q[3]!) * Math.exp(q[5]!) * Math.cos(q[4]!);
  for (let retry = 0; retry < 8; retry++) {
    const V = v0.map((v) => v * T),
      A = a0.map((v) => v * T * T);
    const val = (j: number, x: number) =>
      q0[j]! +
      V[j]! * x +
      0.5 * A[j]! * x * x +
      (10 * dq[j]! - 6 * V[j]! - 1.5 * A[j]!) * x ** 3 +
      (-15 * dq[j]! + 8 * V[j]! + 1.5 * A[j]!) * x ** 4 +
      (6 * dq[j]! - 3 * V[j]! - 0.5 * A[j]!) * x ** 5;
    let ok = true;
    for (let j = 0; j < END_COORDS; j++) cp[j] = val(j, 0);
    for (let i = 1; i <= END_SAMPLES && ok; i++) {
      for (let j = 0; j < END_COORDS; j++) c[j] = val(j, i / END_SAMPLES);
      const dτ = T / END_SAMPLES,
        dMid = Math.exp((c[5]! + cp[5]!) / 2),
        qRate = Math.hypot(c[0]! - cp[0]!, c[1]! - cp[1]!, c[2]! - cp[2]!) / dτ / dMid,
        cRate = Math.hypot(camX(c) - camX(cp), camY(c) - camY(cp), camZ(c) - camZ(cp)) / dτ / dMid;
      if (
        Math.abs(c[3]! - cp[3]!) / dτ > L.yaw ||
        Math.abs(c[4]! - cp[4]!) / dτ > L.pitch ||
        Math.abs(c[5]! - cp[5]!) / dτ > L.logd ||
        qRate > L.target ||
        cRate > L.camera
      )
        ok = false;
      for (let j = 0; j < END_COORDS; j++) cp[j] = c[j]!;
    }
    if (ok) break;
    T *= 1.25;
  }
  return { q0, dq, v: v0.map((v) => v * T), a: a0.map((v) => v * T * T), dur: T };
}

/** 回程上 τ 秒的镜头写进 out；τ ≥ dur 时直接复制出发的 position、target（不经三角函数重建） */
export function glimpseReturnCamera(ret: GlimpseReturn, from: GlimpsePose, tau: number, out: GlimpsePose): void {
  if (tau >= ret.dur) {
    out.pos.x = from.pos.x;
    out.pos.y = from.pos.y;
    out.pos.z = from.pos.z;
    out.target.x = from.target.x;
    out.target.y = from.target.y;
    out.target.z = from.target.z;
    return;
  }
  const x = clamp(tau / ret.dur, 0, 1),
    x2 = x * x,
    x3 = x2 * x,
    x4 = x3 * x,
    x5 = x4 * x,
    { q0, dq, v: V, a: A } = ret;
  const val = (j: number) =>
    q0[j]! + V[j]! * x + 0.5 * A[j]! * x2 + (10 * dq[j]! - 6 * V[j]! - 1.5 * A[j]!) * x3 + (-15 * dq[j]! + 8 * V[j]! + 1.5 * A[j]!) * x4 + (6 * dq[j]! - 3 * V[j]! - 0.5 * A[j]!) * x5;
  const o = BASE;
  o.x = val(0);
  o.y = val(1);
  o.z = val(2);
  o.yaw = val(3);
  o.pitch = val(4);
  o.d = Math.exp(val(5));
  rigToPose(o, out);
}
